import { Dialog } from '@headlessui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { Button, TopImage } from '@project/components/atoms';
import { LoadingContainer } from '@project/components/templates';
import { useGetShowcase } from '@project/queries';
import { concat, map } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { useNavigate, useParams } from 'react-router-dom';

type MediaItem = {
  type: 'image' | 'video';
  src: string;
};

export const ShowShowcasePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [isOpen, setIsOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const touchStartX = useRef<number | null>(null);
  const touchDeltaX = useRef<number>(0);
  const swipeThreshold = 50;

  const { data: showcase, isLoading } = useGetShowcase(id);

  const goBack = () => navigate(-1);

  const allMedia: MediaItem[] = useMemo(() => {
    const images: MediaItem[] = (concat(showcase?.featureImage, showcase?.images) as string[])
      .filter(Boolean)
      .map((src) => ({ type: 'image', src }));

    const videos: MediaItem[] = (showcase?.videos || [])
      .filter(Boolean)
      .map((src: string) => ({ type: 'video', src }));

    return [...images, ...videos];
  }, [showcase]);

  const totalItems = allMedia.length;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
  };

  const getItemIndex = (offset: number) => {
    return (currentIndex + offset + totalItems) % totalItems;
  };

  const handleIsOpen = (index: number) => {
    setActiveImage(index);
    setIsOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchDeltaX.current > swipeThreshold) {
      handlePrev();
    } else if (touchDeltaX.current < -swipeThreshold) {
      handleNext();
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
  }, [totalItems]);

  const renderMediaItem = (item: MediaItem, index: number) => {
    if (item.type === 'video') {
      return (
        <video className="h-full w-full object-cover" src={item.src} key={index}>
          Your browser does not support the video tag.
        </video>
      );
    }
    return <img className="h-full w-full object-cover" src={item.src} alt="Showcase" key={index} />;
  };

  const ExpandedModal = useCallback(
    () => (
      <Dialog
        open={isOpen}
        onClose={handleClose}
        className="fixed inset-4 z-50 mx-auto my-auto flex items-center justify-center md:inset-10"
      >
        <div className="fixed inset-0 z-50 flex bg-black/80">
          <div className="m-auto flex items-center justify-center">
            <Dialog.Panel className="h-[50vh] w-[90vw] overflow-hidden md:h-[50vh] md:w-[500px]">
              <Carousel
                autoPlay={false}
                infiniteLoop={false}
                showStatus={false}
                showIndicators={false}
                showThumbs={false}
                showArrows
                selectedItem={activeImage}
              >
                {map(allMedia, (item, index) =>
                  item.type === 'video' ? (
                    <video src={item.src} controls key={index}>
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img src={item.src} alt="Expanded image" key={index} />
                  ),
                )}
              </Carousel>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    ),
    [isOpen, activeImage, allMedia],
  );

  return (
    <div className="mb-20">
      <TopImage />
      <LoadingContainer isLoading={isLoading}>
        <div className="flex flex-col">
          <div className="mt-20 mb-14 md:mx-10 lg:my-14">
            <div className="flex flex-row items-center">
              <Button style="bg-gray-200 rounded md:ml-2 ml-4" onClick={goBack}>
                <ChevronLeftIcon className="w-8" />
              </Button>
              <div className="ml-4 text-xl font-medium underline decoration-gold decoration-2 underline-offset-4 md:text-2xl">
                {showcase?.name}
              </div>
            </div>
            <p className="mx-6 mt-20 text-lg leading-loose tracking-wide md:mx-14 md:mt-14 md:text-justify xl:mx-[10%]">
              {showcase?.description}
            </p>
          </div>

          {totalItems > 0 && (
            <div className="mt-6 bg-black px-2 py-10 md:px-10">
              <div
                className="relative flex items-center justify-center"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <button
                  onClick={handlePrev}
                  className="absolute left-2 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white transition hover:bg-white/40 md:left-6"
                  aria-label="Previous"
                >
                  <ChevronLeftIcon className="w-6" />
                </button>

                <div className="flex w-full items-center justify-center gap-4 overflow-hidden py-6 md:gap-6">
                  {totalItems > 1 && (
                    <div
                      className="hidden w-[200px] shrink-0 cursor-pointer opacity-50 transition-all duration-300 md:block md:w-[240px]"
                      style={{ transform: 'scale(0.85) translateX(20px)' }}
                      onClick={handlePrev}
                    >
                      <div className="h-[180px] overflow-hidden border-y-2 border-gold md:h-[220px]">
                        {renderMediaItem(allMedia[getItemIndex(-1)], getItemIndex(-1))}
                      </div>
                    </div>
                  )}

                  <div
                    className="z-20 w-[280px] shrink-0 cursor-pointer transition-all duration-300 md:w-[360px]"
                    style={{ transform: 'scale(1)' }}
                    onClick={() => handleIsOpen(currentIndex)}
                  >
                    <div className="h-[240px] overflow-hidden border-y-2 border-gold md:h-[300px]">
                      {renderMediaItem(allMedia[currentIndex], currentIndex)}
                    </div>
                  </div>

                  {totalItems > 1 && (
                    <div
                      className="hidden w-[200px] shrink-0 cursor-pointer opacity-50 transition-all duration-300 md:block md:w-[240px]"
                      style={{ transform: 'scale(0.85) translateX(-20px)' }}
                      onClick={handleNext}
                    >
                      <div className="h-[180px] overflow-hidden border-y-2 border-gold md:h-[220px]">
                        {renderMediaItem(allMedia[getItemIndex(1)], getItemIndex(1))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleNext}
                  className="absolute right-2 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white transition hover:bg-white/40 md:right-6"
                  aria-label="Next"
                >
                  <ChevronRightIcon className="w-6" />
                </button>
              </div>

              <div className="mt-4 text-center text-sm tracking-wide text-white">
                {currentIndex + 1} of {totalItems}
              </div>
            </div>
          )}

          <ExpandedModal />
        </div>
      </LoadingContainer>
    </div>
  );
};
